import create from 'zustand';
import produce from 'immer';
import arrayMove from 'array-move';
import { nanoid } from 'nanoid';

import DayJs from 'utils/Dayjs';
import { getClosestLastMay } from 'utils/Helpers';

const STUDENT_GRADE_INITIAL = {
	grades: [],
	edited: false
};

export const useStudentGradeStore = create(set => ({
	...STUDENT_GRADE_INITIAL,

	// Handle student's grade state change
	onChangeGrade: (id, { name, value }) => set(produce(state => {
		const grade = state.grades.find(g => g.id === id);
		grade[name] = value;
		state.edited = true;
	})),

	// Handle student's grade addition
	onAddGrade: (grade) => set(produce(state => {
		state.grades.push({
			...grade,
			startDate: DayJs().toISOString(),
			endDate: getClosestLastMay(),
			state: 'assigned',
			courses: [],
			warrants: []
		});
		state.edited = true;
	})),

	// Handle student's grade removal
	onRemoveGrade: (id) => set(produce(state => {
		state.grades = state.grades.filter(grade => grade.id !== id);
	})),

	// Handle student's blank selection
	onSelectBlank: (gradeId, courses) => set(produce(state => {
		const grade = state.grades.find(g => g.id === +gradeId);
		grade.courses = courses.map(course => ({
			...course,
			startDate: '',
			endDate: '',
			takes: course.exam ? [{
				key: nanoid(),
				gradeId: grade.id,
				courseId: course.id,
				examId: course.exam.id,
				startDate: '',
				endDate: '',
				mark: '',
				state: 'assigned',
				exam: course.exam
			}] : []
		}));
		state.edited = true;
	})),

	// Handle student's course addition
	onAddCourse: (gradeId, course) => set(produce(state => {
		const grade = state.grades.find(g => g.id === +gradeId);
		grade.courses.push({
			...course,
			startDate: '',
			endDate: '',
			takes: course.exam ? [{
				key: nanoid(),
				gradeId: grade.id,
				courseId: course.id,
				examId: course.exam.id,
				startDate: '',
				endDate: '',
				state: 'assigned',
				mark: '',
				exam: course.exam
			}] : []
		});
		state.edited = true;
	})),

	// Handle student's course removal
	onRemoveStudentCourse: (gradeId, courseId) => set(produce(state => {
		const grade = state.grades.find(g => g.id === +gradeId);
		grade.courses = grade.courses.filter(c => c.id !== +courseId);
		state.edited = true;
	})),

	// Handle student's course state change
	onChangeStudentCourse: (gradeId, courseId, { name, value }) => set(produce(state => {
		const grade = state.grades.find(g => g.id === +gradeId);
		const course = grade.courses.find(c => c.id === +courseId);
		const take = course.takes.find(t => t.state === 'assigned');
		
		// Update course value
		course[name] = value;

		// Update assigned take's value
		if (take) take[name] = value;

		// Check if selected startDate and its the first day of the month
		if (name === 'startDate' && DayJs(value).date() === 1) {
			// Calculate last day of the month
			const endDate = DayJs(value).endOf('month').endOf('day').toISOString();

			// Update course's end date
			course.endDate = endDate;

			// Update assigned take's end date
			if (take) take.endDate = endDate;
		}

		state.edited = true;
	})),

	// Handle student's course sorting
	onSortStudentCourse: (gradeId, { oldIndex, newIndex }) => set(produce(state => {
		const grade = state.grades.find(g => g.id === +gradeId);
		grade.courses = arrayMove(grade.courses, oldIndex, newIndex);
		state.edited = true;
	})),

	// Handle student's take state change
	onChangeTake: (gradeId, courseId, takeKey, { name, value }) => set(produce(state => {
		const grade = state.grades.find(g => g.id === +gradeId);
		const course = grade.courses.find(c => c.id === +courseId);
		const take = course.takes.find(t => t.key === takeKey);
		take[name] = value;
		state.edited = true;
	})),

	// Handle student's warrant addition
	onAddWarrant: (gradeId, warrant) => set(produce(state => {
		const grade = state.grades.find(g => g.id === +gradeId);
		grade.warrants.push({
			key: nanoid(),
			name: warrant.name,
			gradeId: warrant.gradeId
		});
		state.edited = true;
	})),

	// Handle student's warrant removal
	onRemoveWarrant: (gradeId, warrantKey) => set(produce(state => {
		const grade = state.grades.find(g => g.id === +gradeId);
		grade.warrants = grade.warrants.filter(w => w.key !== warrantKey);
		state.edited = true;
	})),

	// Initialize student's grade state
	onInit: ({ grades, courses, takes, warrants }) => set(produce(state => {
		state.edited = false;
		state.grades = grades.map(grade => ({
			...grade,
			courses: courses.filter(c => c.gradeId === grade.id).map(course => ({
				...course,
				takes: takes.filter(t => t.courseId === course.id).map(take => ({
					...take,
					key: nanoid()
				}))
			})),
			warrants: warrants.filter(w => w.gradeId === grade.id).map(warrant => ({
				...warrant,
				key: nanoid()
			}))
		}));
		const active = state.grades.find(g => g.state === 'active');
		const rest = state.grades.filter(g => g.state !== 'active');
		state.grades = active ? [active, ...rest] : state.grades;
	})),

	// Reset store to initial state
	onReset: () => set({
		...STUDENT_GRADE_INITIAL
	})
}));